import styled from "@emotion/styled";
import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import { useGetCourseCategoryBySlug } from "../../hooks/queries/useGetCourseCategoryBySlug";
import { useSubCategories } from "../../hooks/queries/useSubCategories";
import { colors } from "../../types";
import HeaderImage from "../header/HeaderImage";
import Breadcrumb from "../misc/Breadcrumb";
import ErrorMessage from "../misc/ErrorMessage";
import InfoSection from "../misc/InfoSection";
import LoadingDots from "../misc/LoadingDots";
import { PageWrapper } from "../misc/PageWrapper";
import { CourseCardsGrid } from "./CourseCardsGrid";
import CourseCategoryCard from "./CourseCategoryCard";
import { CoursesWrapper } from "./CoursesWrapper";

const ApprenticeshipsBanner = styled("div")({
  margin: "1rem auto",
  padding: "1.5rem 1rem",
  background: colors.brand.brandDarkRed.rgbaString({ alpha: 1 }),
  borderRadius: "12px",
  maxWidth: "1200px",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  border: `1px solid ${colors.brand.brandBlack.rgbaString({ alpha: 0.12 })}`,
  "@media (max-width: 768px)": {
    padding: "1rem",
  },
  "@media (max-width: 480px)": {
    padding: "0.75rem",
  },
});

const ApprenticeshipText = styled("p")({
  fontSize: "0.9rem",
  lineHeight: "1.5",
  color: "#ffffff",
  textAlign: "center",
  margin: 0,
  fontWeight: 300,
  letterSpacing: "0.3px",
  textShadow: "0 2px 4px rgba(0, 0, 0, 0.5)",
  "@media (max-width: 768px)": {
    fontSize: "0.85rem",
    lineHeight: "1.4",
  },
  "@media (max-width: 480px)": {
    fontSize: "0.78rem",
    lineHeight: "1.3",
  },
});

const HighlightText = styled("strong")({
  color: "#ffd700",
  fontWeight: 700,
  fontSize: "1.02em",
  textShadow: "0 0 10px rgba(255, 215, 0, 0.3)",
});

const CourseCategoriesPage: React.FC = () => {
  const { categorySlug = "" } = useParams();
  const navigate = useNavigate();

  const {
    data: category,
    isLoading: isCategoryLoading,
    isError: isCategoryError,
  } = useGetCourseCategoryBySlug(categorySlug, "true");

  const {
    data: subCategories = [],
    isLoading: isLoadingSubCategories,
    isError: isErrorSubCategories,
    refetch: fetchSubCategories,
  } = useSubCategories(category?.categoryId || "");

  useEffect(() => {
    if (category) {
      fetchSubCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  if (isErrorSubCategories) {
    return <ErrorMessage message={"Error fetching sub-categories"} />;
  }

  if (isCategoryError) {
    return <ErrorMessage message={"Error fetching category"} />;
  }

  if (isCategoryLoading || isLoadingSubCategories) {
    return (
      <div>
        <LoadingDots />
      </div>
    );
  }
  return (
    <>
      <Helmet>
        <title>{`DTN - ${category?.name}`}</title>
        <meta
          name="description"
          content="Digital Telecoms Network Academy provides a comprehensive learning experience for the Telecommunications Industry, with blended practical training to enhance or embrace new skills for the ongoing Digital Development for the Smart City Infrastructure"
        />
      </Helmet>
      <PageWrapper>
        <Breadcrumb
          items={[
            { label: "Home", href: "/" },
            { label: "Our Courses", href: "/our-courses" },
            { label: `${category?.name}` },
          ]}
        />
        {category?.headerImageUrl && (
          <HeaderImage
            backgroundImageUrl={category.headerImageUrl.replace(/ /g, "%20")}
          />
        )}
        <InfoSection />
        {categorySlug === "apprenticeships" && (
          <ApprenticeshipsBanner>
            <ApprenticeshipText>
              DTN Academy recently received an{" "}
              <HighlightText>'Outstanding'</HighlightText> rating from Ofsted
              for apprenticeships. They praised the academy for enabling
              apprentices to quickly develop highly sought-after knowledge and
              skills, making them valuable assets to their employers.
              Impressively, almost all apprentices not only complete their
              apprenticeship but also go on to achieve a{" "}
              <HighlightText>distinction</HighlightText> grade.
            </ApprenticeshipText>
          </ApprenticeshipsBanner>
        )}
        <div style={{ display: "flex", justifyContent: "center" }}>
          <CoursesWrapper>
            <CourseCardsGrid>
              {subCategories.map((subCategory) => (
                <CourseCategoryCard
                  key={subCategory.subCategoryId}
                  imageUrl={subCategory.imageUrl}
                  logoUrl="/logo.png"
                  title={subCategory.name}
                  onClick={() =>
                    navigate(
                      `/our-courses/${category?.slug}/${subCategory.slug}`
                    )
                  }
                />
              ))}
            </CourseCardsGrid>
          </CoursesWrapper>
        </div>
      </PageWrapper>
    </>
  );
};

export default CourseCategoriesPage;
